import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';
import { Text } from '@famly/mf_data-display_text';
import { Box } from '@famly/mf_layout_box';
import { Stack } from '@famly/mf_layout_stack';
import { useBreakpoints } from '@famly/mf_util_breakpoints';
import * as PrivateRouteSelectors from 'signin-app/components/private-route/selectors';
import * as LoginSelectors from 'signin-app/login/selectors';
import * as CheckinSelectors from 'signin-app/employee/check-in/selectors';
import translate from 'signin-app/helpers/translate';
import * as GroupSelectors from 'signin-app/groups/selectors';
import * as CheckoutSelectors from 'signin-app/child/check-out/selectors';
import Header from 'signin-app/components/header';
import { PinHeader } from 'signin-app/child/pin-header';
import { ConfirmButton, ActionButton as CancelButton, BottomBarContainer, PageBodyContainer, } from 'signin-app/components/action-buttons';
import { isPinApp } from 'signin-app/login/selectors';
import { pinHolder } from 'signin-app/pin/selectors';
import RoutesMap from 'signin-app/routes/routes-map';
import { HeaderPerson } from 'signin-app/components/header-person';
import { Card, CardsContainer } from 'signin-app/groups/group-item';
import useTypedSelector from 'signin-app/components/hooks/use-typed-selector';
import PersonSelect from 'signin-app/components/person-select';
import * as ChildSelectors from 'signin-app/child/selectors';
import { useWhitelabelConfiguration } from 'signin-app/whitelabel/use-whitelabel-configuration';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { useQueryParams } from 'web-app/react/hooks/use-query-params';
import { PageContainer } from 'signin-app/components/containers';
import { getInitials } from 'signin-app/util/get-initials';
import { isBeforeNow } from 'signin-app/groups/helpers';
import { useUpdateError } from '@famly/stat_ts-utils_global-messages';
import { defaultErrorDuration } from 'web-app/react/global-event/helpers/constants';
import { useDisplayName } from 'signin-app/helpers/use-display-name';
import { Spinner } from '@famly/mf_feedback_spinner';
import { resetLeave, selectGroup, checkInEmployee } from './actions';
import LeaveTime from './leave-time';
export const EmployeeCheckIn = (props) => {
    const accessToken = useTypedSelector(LoginSelectors.accessToken);
    const updateError = useUpdateError();
    const isFetching = useTypedSelector(PrivateRouteSelectors.isPrefetching);
    const employee = useTypedSelector(state => CheckinSelectors.employee(state, props));
    const selectedHours = useTypedSelector(CheckinSelectors.selectedHours);
    const selectedMinutes = useTypedSelector(CheckinSelectors.selectedMinutes);
    const selectedGroupId = useTypedSelector(CheckinSelectors.selectedGroupId);
    const groups = useTypedSelector(GroupSelectors.groups);
    const isCheckingIn = useTypedSelector(CheckinSelectors.isCheckingIn);
    const canCheckIn = useTypedSelector(CheckinSelectors.canCheckIn);
    const limitedData = useTypedSelector(CheckoutSelectors.limitedData);
    const employeeEstimatedCheckoutTimeEnabled = useTypedSelector(GroupSelectors.employeeEstimatedCheckoutTimeEnabled);
    const isPin = useTypedSelector(isPinApp);
    const pin = useTypedSelector(pinHolder);
    const whitelabelConfiguration = useWhitelabelConfiguration();
    const pinChildren = useTypedSelector(ChildSelectors.pinChildren);
    const { isTabletLandscapeAndLarger } = useBreakpoints();
    const timezone = useTypedSelector(GroupSelectors.timezone);
    const displayName = useDisplayName(employee === null || employee === void 0 ? void 0 : employee.name.firstName, employee === null || employee === void 0 ? void 0 : employee.name.lastName);
    const dispatch = useDispatch();
    const { update } = useQueryParams(['update']);
    const isUpdatePage = hasValue(update);
    React.useEffect(() => {
        if (employee && !employee.checkedIn) {
            dispatch(resetLeave.action());
        }
        if (employee && (employee.groupId || employee.tempGroupId)) {
            const currentEmployeeGroupId = employee.tempGroupId ? employee.tempGroupId : employee.groupId;
            dispatch(selectGroup.action(currentEmployeeGroupId));
        }
    }, [dispatch, employee]);
    const handleEmployeeCheckIn = () => {
        if (employee) {
            const successMessage = translate(employee.checkedIn ? 'updateSignInSuccess' : 'signInSuccess', {
                personName: displayName,
            });
            const pastLeavingTimeSelected = isBeforeNow(timezone, selectedHours, selectedMinutes);
            if (pastLeavingTimeSelected) {
                updateError(translate('changeLeavingTimeError'), { duration: defaultErrorDuration });
                return;
            }
            dispatch(checkInEmployee.action(accessToken, employee.id, pin, selectedHours, selectedMinutes, selectedGroupId, successMessage, employee.checkedIn));
        }
    };
    const employeeName = React.useMemo(() => {
        return limitedData ? employee === null || employee === void 0 ? void 0 : employee.name.firstName : employee === null || employee === void 0 ? void 0 : employee.name.fullName;
    }, [employee, limitedData]);
    const initials = React.useMemo(() => {
        if (!employeeName) {
            return '';
        }
        return getInitials(employeeName);
    }, [employeeName]);
    const employeeInfo = React.useMemo(() => {
        if (!employee || !employeeName) {
            return null;
        }
        return _jsx(HeaderPerson, { image: employee === null || employee === void 0 ? void 0 : employee.image.small, initials: initials, name: employeeName });
    }, [employee, initials, employeeName]);
    // Show employee info header only if PersonSelect will not be shown
    const shouldShowEmployeeInfoOnPin = React.useMemo(() => {
        if (!isPin || (pinChildren && pinChildren.size > 0)) {
            return false;
        }
        return true;
    }, [isPin, pinChildren]);
    const cancelButtonLink = React.useMemo(() => {
        if (isUpdatePage) {
            return `/employee/${employee === null || employee === void 0 ? void 0 : employee.id}/checkout`;
        }
        return RoutesMap.overview;
    }, [isUpdatePage, employee === null || employee === void 0 ? void 0 : employee.id]);
    return (_jsxs(PageContainer, { children: [isPin ? (_jsx(PinHeader, { whitelabelConfiguration: whitelabelConfiguration })) : (_jsx(Header, { centerElement: employeeInfo })), isFetching || !employee ? (_jsx(Stack, { justifyContent: "center", width: "100%", children: _jsx(Spinner, { size: 32, "aria-label": i18next.t($ => $.react.components.loading.spinner.translations.json.loadingAriaLabel) }) })) : (_jsxs(_Fragment, { children: [_jsxs(PageBodyContainer, { mt: 16, children: [shouldShowEmployeeInfoOnPin ? (_jsx(Stack, { justifyContent: "center", mb: 14, children: employeeInfo })) : null, _jsx(PersonSelect, { activeEmployeeId: employee.id }), _jsxs(Stack, { flexDirection: isTabletLandscapeAndLarger ? 'row' : 'column', gap: 12, children: [!isUpdatePage ? (_jsxs(Box, { flex: 1, children: [_jsx(Text, { variant: "h6", color: "n400", emphasized: true, "data-e2e-id": "signin-room-title", children: translate('whichRoomToSignInto') }), _jsx(CardsContainer, { mt: 2, isCompact: true, children: groups.map(group => (_jsx(Card, { image: group.image.small, displayTitle: group.title, selected: group.groupId === selectedGroupId, onClick: () => dispatch(selectGroup.action(group.groupId)), dataE2eCardContainerClass: `group-title-${group.title}`, alignment: "vertical" }, group.groupId))) })] })) : null, employeeEstimatedCheckoutTimeEnabled ? (_jsx(Box, { flex: 1, children: _jsx(LeaveTime, { checkedIn: employee.checkedIn, selectedLeaveTime: employee.estimatedCheckoutTime }) })) : null] })] }), _jsxs(BottomBarContainer, { children: [!isPin ? _jsx(CancelButton, { text: translate('cancel'), link: cancelButtonLink }) : null, _jsx(ConfirmButton, { type: 'signIn', disabled: !canCheckIn, isLoading: isCheckingIn, onClick: handleEmployeeCheckIn, text: employee.checkedIn
                                    ? translate('updateLeavingTime')
                                    : translate('signIn', { personName: displayName }), dataE2EId: "employee-check-in-confirm-button", isOnlyButton: isPin })] })] }))] }));
};
